import { useEffect, useMemo, useState } from "react";

import { useLocalization } from "@fluent/react";
import { entities, modules } from "byzantine";
import { ResponsiveFlex } from "@narmi/design_system";
import useDeliverySpeedOptions from "../ach/useDeliverySpeedOptions";
import InvoiceDeliverySpeedCards from "./InvoiceDeliverySpeedCards";
import { useInvoiceFlow } from "./InvoiceFlowContext";
import styles from "../ach/DeliverySpeedSection.module.scss";

const InvoiceDeliverySpeedSection = () => {
  const { l10n } = useLocalization();
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const { invoice, setDeliveryOptionDisplay } = useInvoiceFlow();
  const holidayDates = entities.holidays.useHolidays();
  const {
    options,
    selectedDeliverySpeed,
    setDeliverySpeed,
    isPastCutoff,
    isResolvingOptions,
  } = useDeliverySpeedOptions();
  const extractedDueDate = entities.invoices.getInvoiceDueDateFormValue(
    invoice?.extracted
  );
  const recommendationDate = useMemo(
    () => extractedDueDate || form.values.startDate,
    [extractedDueDate, form.values.startDate]
  );
  const computedRecommendedSpeed = useMemo(
    () =>
      modules.achPayments.getRecommendedInvoicePaySpeed({
        dueDate: recommendationDate,
        holidayDates,
        isPastCutoff,
        hasSameDayOption: options.some((option) => option.value === "sameDay"),
        hasInstantOption: options.some((option) => option.value === "instant"),
      }),
    [holidayDates, isPastCutoff, options, recommendationDate]
  );
  const [recommendedSpeed, setRecommendedSpeed] = useState(
    computedRecommendedSpeed
  );
  const hasSelectedDeliverySpeed = Boolean(
    form.values.deliverySpeedManuallySelected
  );

  useEffect(() => {
    const option = options.find(
      (candidate) => candidate.value === selectedDeliverySpeed
    );

    setDeliveryOptionDisplay(
      option
        ? {
            arrival: option.arrival,
            fee: option.fee,
            label: option.label,
          }
        : null
    );
  }, [options, selectedDeliverySpeed, setDeliveryOptionDisplay]);

  useEffect(() => {
    if (!hasSelectedDeliverySpeed) {
      setRecommendedSpeed(computedRecommendedSpeed);
    }
  }, [computedRecommendedSpeed, hasSelectedDeliverySpeed]);

  useEffect(() => {
    if (
      isResolvingOptions ||
      hasSelectedDeliverySpeed ||
      !computedRecommendedSpeed
    ) {
      return;
    }

    if (
      selectedDeliverySpeed &&
      (selectedDeliverySpeed !== "standard" ||
        computedRecommendedSpeed === "standard")
    ) {
      return;
    }

    setDeliverySpeed(computedRecommendedSpeed);
  }, [
    computedRecommendedSpeed,
    setDeliverySpeed,
    hasSelectedDeliverySpeed,
    isResolvingOptions,
    selectedDeliverySpeed,
  ]);

  const optionsWithRecommendation = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        badgeLabel:
          option.value === recommendedSpeed
            ? l10n.getString("tag-recommended")
            : undefined,
      })),
    [l10n, options, recommendedSpeed]
  );

  return (
    <ResponsiveFlex gapSize="m">
      <div className={styles.deliverySpeedHeader}>
        <h4 className="fontSize--m nds-sans">
          {l10n.getString("invoice-details-method")}
        </h4>
      </div>
      <InvoiceDeliverySpeedCards
        options={optionsWithRecommendation}
        selectedValue={selectedDeliverySpeed}
        onSelect={setDeliverySpeed}
        isResolvingOptions={isResolvingOptions}
      />
    </ResponsiveFlex>
  );
};

export default InvoiceDeliverySpeedSection;
